/**
 * The product's four layout rulers, in px, for `matchMedia` — the JS mirror of the
 * `--breakpoint-*` tokens in `styles.css` (Tailwind's `sm`/`xl` plus the two the theme
 * declares). `breakpoints.test.ts` keeps both sides equal; change them together.
 *
 * Which ruler decides what:
 *   sm      COMPONENT — a card, a search bar, a stage turns into its phone drawing.
 *   wide    SHELL — sidebar inline vs hamburger, push drawer vs overlay.
 *   column  SIDE COLUMN — trip column / wide board fit next to the conversation.
 *   xl      FULL BOARD.
 *
 * Viewport queries belong to the shell; a component that can live in a column, a drawer
 * or an iframe asks its container (`@container`) or the `sm` stage, never the window.
 */
export const BREAKPOINTS = {
  sm: 640,
  wide: 880,
  column: 1100,
  xl: 1280,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** `(min-width: Npx)` — true from the ruler up. */
export function atLeast(breakpoint: Breakpoint): string {
  return `(min-width: ${String(BREAKPOINTS[breakpoint])}px)`;
}

/**
 * `(max-width: N-0.02px)` — the exact complement of `atLeast` for the same ruler.
 * `matchMedia` gets the legacy syntax on purpose: tests stub it by matching the query
 * string, and the CSS side does not need this helper (it writes `width <` with `theme()`).
 */
export function below(breakpoint: Breakpoint): string {
  return `(max-width: ${String(BREAKPOINTS[breakpoint] - 0.02)}px)`;
}
